import React, { useState } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, ChevronDown, ChevronRight, Beaker, Plus, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TestRunner from './TestRunner';
import EditableTestCase from './EditableTestCase';
import AssetTestRunner from './AssetTestRunner';

interface RuleTest {
  input: string;
  expected: 'pass' | 'fail';
  description: string;
}

interface BRDRule {
  id: number;
  rule: string;
  severity: string;
  field: string;
  section: string;
  channel: string | string[];
  enabled: boolean;
  tests?: RuleTest[];
}

interface RuleTestsPanelProps {
  rule: BRDRule;
  onUpdateTests?: (ruleId: number, tests: RuleTest[]) => void;
}

export default function RuleTestsPanel({ rule, onUpdateTests }: RuleTestsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tests, setTests] = useState<RuleTest[]>(rule.tests || []);
  const [showAssetTester, setShowAssetTester] = useState(false);

  const handleUpdateTest = (index: number, updatedTest: RuleTest) => {
    const newTests = [...tests];
    newTests[index] = updatedTest;
    setTests(newTests);
    if (onUpdateTests) {
      onUpdateTests(rule.id, newTests);
    }
  };

  const handleDeleteTest = (index: number) => {
    const newTests = tests.filter((_, i) => i !== index);
    setTests(newTests);
    if (onUpdateTests) {
      onUpdateTests(rule.id, newTests);
    }
  };

  const handleAddTest = () => {
    const newTest: RuleTest = {
      input: '',
      expected: 'pass',
      description: 'New test case'
    };
    const newTests = [...tests, newTest];
    setTests(newTests);
    if (onUpdateTests) {
      onUpdateTests(rule.id, newTests);
    }
  };

  if (tests.length === 0) {
    return (
      <div className="mt-3 border-t border-gray-200 pt-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Beaker className="w-4 h-4" />
            <span>No test cases defined</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleAddTest}>
            <Plus className="w-3 h-3 mr-1" />
            Add Test
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Beaker className="w-4 h-4" />
          <span>Test Cases ({tests.length})</span>
        </button>

        {isExpanded && (
          <Button variant="ghost" size="sm" onClick={handleAddTest}>
            <Plus className="w-3 h-3 mr-1" />
            Add Test
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* Asset Testing */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowAssetTester(!showAssetTester)}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:underline mb-3"
            >
              <Upload className="w-4 h-4" />
              {showAssetTester ? 'Hide' : 'Show'} Real Asset Testing
            </button>
            
            {showAssetTester && (
              <AssetTestRunner
                ruleId={rule.id}
                rule={rule.rule}
                field={rule.field}
                severity={rule.severity}
                testCases={tests}
              />
            )}
          </div>

          {/* Test Runner */}
          <TestRunner
            ruleId={rule.id}
            rule={rule.rule}
            field={rule.field}
            testCases={tests}
          />

          {/* Editable Test Cases */}
          <div className="space-y-2">
            <p className="text-xs text-gray-500 mb-3">
              Configure test cases to validate this rule's logic
            </p>
            
            {tests.map((test, index) => (
              <EditableTestCase
                key={index}
                testCase={test}
                onSave={(updatedTest) => handleUpdateTest(index, updatedTest)}
                onDelete={() => handleDeleteTest(index)}
              />
            ))}
          </div>

          {/* Help Section */}
          <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 dark:text-blue-400">
                <p className="font-medium mb-1">How to use these tests:</p>
                <ul className="list-disc list-inside space-y-1 ml-1">
                  <li>Upload a real asset to test against this rule</li>
                  <li>Click "Run All Tests" to validate with mock data</li>
                  <li>Edit test cases by clicking the edit icon</li>
                  <li>Add new tests to cover more scenarios</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

